import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTree } from '@fluidframework/react/alpha';
import { Add, Close, Delete, DragIndicator } from '@mui/icons-material';
import { Box, IconButton, List, ListItem, ListItemText, ListSubheader, TextField as MuiTextField, Typography, } from '@mui/material';
import React from 'react';
export function EditableStringList(props) {
    const { items, canEdit = false, title, placeholder = 'Add item' } = props;
    useTree(items);
    const [showInsertForm, setShowInsertForm] = React.useState(false);
    const [draggingIndex, setDraggingIndex] = React.useState(undefined);
    const [dragOverIndex, setDragOverIndex] = React.useState(undefined);
    if (!canEdit) {
        if (items.length === 0) {
            return _jsx(_Fragment, {});
        }
        return (_jsx(List, { dense: true, subheader: title && (_jsx(ListSubheader, { component: "div", disableSticky: true, children: title })), children: [...items].map((item, index) => (_jsx(ListItem, { disableGutters: true, children: _jsx(ListItemText, { primary: item }) }, `${item}-${index}`))) }));
    }
    const handleDragStart = (index, ev) => {
        setDraggingIndex(index);
        ev.dataTransfer.effectAllowed = 'move';
        ev.dataTransfer.setData('text/plain', index.toString());
    };
    const handleDragOver = (index, ev) => {
        ev.preventDefault();
        if (draggingIndex !== undefined && index !== draggingIndex) {
            setDragOverIndex(index);
        }
    };
    const handleDrop = (index, ev) => {
        ev.preventDefault();
        if (draggingIndex !== undefined && draggingIndex !== index) {
            let targetIndex = index;
            if (draggingIndex < index) {
                targetIndex++;
            }
            items.moveToIndex(targetIndex, draggingIndex);
        }
        setDraggingIndex(undefined);
        setDragOverIndex(undefined);
    };
    const handleDragEnd = () => {
        setDraggingIndex(undefined);
        setDragOverIndex(undefined);
    };
    const handleRemove = (index) => {
        items.removeAt(index);
    };
    const handleAdd = (value) => {
        const trimmed = value.trim();
        if (trimmed) {
            items.insertAtEnd(trimmed);
        }
        setShowInsertForm(false);
    };
    return (_jsxs(Box, { children: [title && (_jsx(ListSubheader, { component: "div", disableSticky: true, children: title })), _jsx(List, { dense: true, children: [...items].map((item, index) => {
                    const isDragging = index === draggingIndex;
                    const isDragOver = index === dragOverIndex && draggingIndex !== undefined;
                    return (_jsxs(Box, { draggable: true, onDragStart: (ev) => handleDragStart(index, ev), onDragOver: (ev) => handleDragOver(index, ev), onDrop: (ev) => handleDrop(index, ev), onDragEnd: handleDragEnd, sx: {
                            display: 'flex',
                            alignItems: 'center',
                            border: isDragOver ? '1px dashed #999' : '1px solid transparent',
                            opacity: isDragging ? 0.5 : 1,
                            transition: 'border 0.15s ease',
                            py: 0.5,
                        }, children: [_jsx(IconButton, { size: "small", disableRipple: true, sx: {
                                    cursor: 'grab',
                                    mr: 1,
                                    backgroundColor: 'transparent',
                                    '&:hover': { backgroundColor: 'transparent' },
                                }, "aria-label": "drag-handle", children: _jsx(DragIndicator, { fontSize: "small" }) }), _jsx(Typography, { variant: "body2", sx: { flexGrow: 1 }, children: item }), _jsx(IconButton, { size: "small", onClick: () => handleRemove(index), "aria-label": "remove", children: _jsx(Delete, { fontSize: "small" }) })] }, `${item}-${index}`));
                }) }), !showInsertForm && (_jsx(IconButton, { onClick: () => setShowInsertForm(true), "aria-label": "add", children: _jsx(Add, {}) })), showInsertForm && (_jsx(StringInsertForm, { placeholder: placeholder, onAdd: handleAdd, onClose: () => setShowInsertForm(false) }))] }));
}
function StringInsertForm(props) {
    const { placeholder, onAdd, onClose } = props;
    const [value, setValue] = React.useState('');
    const handleAdd = React.useCallback(() => {
        onAdd(value);
        setValue('');
    }, [value, onAdd]);
    const handleKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
        else if (event.key === 'Enter') {
            event.preventDefault();
            handleAdd();
        }
    }, [onClose, handleAdd]);
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, p: 1 }, children: [_jsx(MuiTextField, { size: "small", value: value, onChange: (e) => setValue(e.target.value), onKeyDown: handleKeyDown, placeholder: placeholder, autoFocus: true, fullWidth: true }), _jsx(IconButton, { size: "small", onClick: handleAdd, color: "primary", disabled: !value.trim(), "aria-label": "add", children: _jsx(Add, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", onClick: onClose, "aria-label": "cancel", children: _jsx(Close, { fontSize: "small" }) })] }));
}
//# sourceMappingURL=EditableStringList.js.map